import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Fixtures for the NewsPage widget. The user's priority order:
//   1. Upcoming matches for FAVOURITE teams only — kickoff ascending
//      (soonest first) so the next match the user cares about is the
//      first card.
//   2. Live matches across all leagues — these are time-sensitive,
//      surface them prominently.
//   3. Finished matches from the last 48h — kickoff descending
//      (most recent result first).
//
// Two SELECTs in parallel (cheaper than a single .or() with all the
// status / favourite-team conditions, and keeps the priority logic
// readable). Returns an empty array when nothing matches so the
// widget hides itself on a quiet day with no favs.
export function useRecentFixtures({ favouriteTeamIds, limit = 12 } = {}) {
  const { user } = useAuth()
  const [fixtures, setFixtures] = useState([])
  const [loading, setLoading] = useState(true)

  const favKey = useMemo(
    () => (favouriteTeamIds && favouriteTeamIds.size ? [...favouriteTeamIds].sort().join(',') : ''),
    [favouriteTeamIds],
  )

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    const SELECT = 'id, api_id, kickoff_at, status, elapsed_minutes, home_team_id, home_team, home_logo_url, home_score, away_team_id, away_team, away_logo_url, away_score, league:sports_leagues(slug, name, name_ar, logo_url)'
    const recentSince = new Date(Date.now() - 48 * 3600 * 1000).toISOString()
    const futureUntil = new Date(Date.now() + 14 * 24 * 3600 * 1000).toISOString()
    const nowIso = new Date().toISOString()
    const favIds = favKey ? favKey.split(',').map((s) => parseInt(s, 10)) : []

    // Two queries in parallel:
    //   - Favourite-team upcoming (only if the user has any favs)
    //   - Recent / live (always)
    const upcomingPromise = favIds.length
      ? supabase
          .from('sports_fixtures')
          .select(SELECT)
          .eq('status', 'scheduled')
          .gte('kickoff_at', nowIso)
          .lte('kickoff_at', futureUntil)
          .or(`home_team_id.in.(${favIds.join(',')}),away_team_id.in.(${favIds.join(',')})`)
          .order('kickoff_at', { ascending: true })
          .limit(limit)
      : Promise.resolve({ data: [] })
    const recentPromise = supabase
      .from('sports_fixtures')
      .select(SELECT)
      .or(`status.eq.live,and(status.eq.finished,kickoff_at.gte.${recentSince})`)
      .order('kickoff_at', { ascending: false })
      .limit(limit * 2)

    const [upcomingRes, recentRes] = await Promise.all([upcomingPromise, recentPromise])
    const upcoming = Array.isArray(upcomingRes?.data) ? upcomingRes.data : []
    const recent = Array.isArray(recentRes?.data) ? recentRes.data : []

    // Bucket the recent rows: live first, then finished. Inside each
    // bucket the original ordering (kickoff desc) is preserved.
    const live = []
    const finished = []
    for (const f of recent) {
      if (f.status === 'live') live.push(f)
      else finished.push(f)
    }

    // Dedup by api_id across all three groups — an upcoming match
    // that flipped to live between the two queries shouldn't appear
    // twice. First-seen wins, which preserves the favourite-upcoming
    // priority.
    const seen = new Set()
    const merged = []
    for (const f of [...upcoming, ...live, ...finished]) {
      if (seen.has(f.api_id)) continue
      seen.add(f.api_id)
      merged.push(f)
      if (merged.length >= limit) break
    }
    setFixtures(merged)
    setLoading(false)
  }, [user, favKey, limit])

  useEffect(() => { fetch() }, [fetch])

  return { fixtures, loading, refetch: fetch }
}

// Per-user favourite teams. Same optimistic-mutation shape as the
// league favourites — denormalises name + crest so the chip / hero
// can render without an extra round-trip to a teams table (which we
// don't have; football-data is the source of truth for team metadata).
export function useFavouriteTeams() {
  const { user } = useAuth()
  const [teams, setTeams] = useState([])     // [{ team_id, team_name, team_short_name, team_crest_url }]
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    const { data, error } = await supabase
      .from('sports_user_teams')
      .select('team_id, team_name, team_short_name, team_crest_url')
    if (!error && data) setTeams(data)
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  const teamIds = useMemo(() => new Set(teams.map((t) => t.team_id)), [teams])
  const isFavourite = useCallback((teamId) => teamIds.has(teamId), [teamIds])

  const toggle = useCallback(async ({ id, name, shortName, crestUrl }) => {
    if (!user || !id) return
    const wasFavourite = teamIds.has(id)
    // Optimistic mutate.
    if (wasFavourite) {
      setTeams((prev) => prev.filter((t) => t.team_id !== id))
    } else {
      setTeams((prev) => [...prev, { team_id: id, team_name: name, team_short_name: shortName, team_crest_url: crestUrl }])
    }
    try {
      if (wasFavourite) {
        const { error } = await supabase
          .from('sports_user_teams')
          .delete()
          .eq('team_id', id)
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('sports_user_teams')
          .upsert(
            {
              user_id: user.id,
              team_id: id,
              team_name: name,
              team_short_name: shortName ?? null,
              team_crest_url: crestUrl ?? null,
            },
            { onConflict: 'user_id,team_id', ignoreDuplicates: false },
          )
        if (error) throw error
      }
    } catch (_e) {
      // Rollback on failure — refetch is the simplest correct answer.
      fetch()
    }
  }, [user, teamIds, fetch])

  return { teams, teamIds, isFavourite, loading, toggle, refetch: fetch }
}

// Match details (proxy to football-data /matches/{id} + /head2head).
// One-shot — the page calls this on mount and the result lives until
// the user navigates away. No realtime updates yet; a future tick
// could poll when the match is in progress.
export function useMatchDetails(matchId) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!matchId) {
      setLoading(false)
      return undefined
    }
    let alive = true
    setLoading(true)
    setError(null)
    supabase.functions
      .invoke('get-match', { body: { match_id: Number(matchId), h2h: true, h2h_limit: 5 } })
      .then((res) => {
        if (!alive) return
        if (res.error) setError(res.error.message || 'failed to load match')
        else if (res.data?.error) setError(res.data.error)
        else setData(res.data)
      })
      .catch((e) => { if (alive) setError(e?.message || 'failed to load match') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [matchId])

  return { data, loading, error }
}

// Team details + recent/upcoming matches (proxy to football-data
// /teams/{id} + /teams/{id}/matches). One-shot fetch on mount; the
// response shape mirrors football-data's so the page can render the
// upstream fields directly without intermediate normalisation.
export function useTeamDetails(teamId) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!teamId) {
      setLoading(false)
      return undefined
    }
    let alive = true
    setLoading(true)
    setError(null)
    supabase.functions
      .invoke('get-team', { body: { team_id: Number(teamId), matches_limit: 10 } })
      .then((res) => {
        if (!alive) return
        if (res.error) setError(res.error.message || 'failed to load team')
        else if (res.data?.error) setError(res.data.error)
        else setData(res.data)
      })
      .catch((e) => { if (alive) setError(e?.message || 'failed to load team') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [teamId])

  return { data, loading, error }
}

// Top scorers per competition (proxy to football-data
// /competitions/{code}/scorers). Free tier returns up to 10 rows.
export function useScorers(competitionCode) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!competitionCode) {
      setLoading(false)
      return undefined
    }
    let alive = true
    setLoading(true)
    setError(null)
    supabase.functions
      .invoke('get-scorers', { body: { competition_code: competitionCode, limit: 10 } })
      .then((res) => {
        if (!alive) return
        if (res.error) setError(res.error.message || 'failed to load scorers')
        else if (res.data?.error) setError(res.data.error)
        else setData(res.data)
      })
      .catch((e) => { if (alive) setError(e?.message || 'failed to load scorers') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [competitionCode])

  return { data, loading, error }
}

// News articles related to a match. Queries the existing
// news_articles table for rows whose title or summary mentions
// either team's short name. Fuzzy by ILIKE since cross-language
// transliterations (مانشستر سيتي vs Manchester City) are common —
// missing some Arabic articles is fine for a personal feed; we
// don't promise editorial coverage. Returns at most `limit` rows
// from the last 14 days, newest first.
export function useMatchNews({ homeName, awayName, homeShortName, awayShortName }, limit = 5) {
  const { user } = useAuth()
  const [articles, setArticles] = useState([])
  const [loading, setLoading] = useState(true)

  // The most distinctive name for each team is usually the shortName
  // (e.g. "Real Madrid" rather than "Real Madrid CF"). Fall back to
  // the full name if shortName isn't present.
  const homeKey = homeShortName || homeName || ''
  const awayKey = awayShortName || awayName || ''

  useEffect(() => {
    if (!supabaseConfigured || !user || !(homeKey || awayKey)) {
      setLoading(false)
      return undefined
    }
    let alive = true
    setLoading(true)
    const since = new Date(Date.now() - 14 * 24 * 3600 * 1000).toISOString()
    // PostgREST's .or() accepts a comma-separated list of conditions.
    // Each ilike literal is wrapped in % wildcards for substring.
    // Quotes inside the literal would need percent-encoding but team
    // names don't include them — safe.
    const conds = []
    if (homeKey) {
      const safe = homeKey.replace(/[,()]/g, '')
      conds.push(`title.ilike.%${safe}%`)
      conds.push(`summary.ilike.%${safe}%`)
    }
    if (awayKey) {
      const safe = awayKey.replace(/[,()]/g, '')
      conds.push(`title.ilike.%${safe}%`)
      conds.push(`summary.ilike.%${safe}%`)
    }
    if (!conds.length) {
      setArticles([])
      setLoading(false)
      return () => { alive = false }
    }
    supabase
      .from('news_articles')
      .select('id, title, summary, image_url, published_at, language, source:news_sources(name, kind)')
      .or(conds.join(','))
      .gte('published_at', since)
      .order('published_at', { ascending: false })
      .limit(limit)
      .then(({ data, error }) => {
        if (!alive) return
        if (!error && Array.isArray(data)) setArticles(data)
        else setArticles([])
        setLoading(false)
      })
    return () => { alive = false }
  }, [user, homeKey, awayKey, limit])

  return { articles, loading }
}

// League standings (proxy to football-data
// /competitions/{code}/standings). competitionCode is the short code
// stored on sports_leagues.provider_code (PL / PD / SA / BL1 / …).
export function useStandings(competitionCode) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!competitionCode) {
      setLoading(false)
      return undefined
    }
    let alive = true
    setLoading(true)
    setError(null)
    supabase.functions
      .invoke('get-standings', { body: { competition_code: competitionCode } })
      .then((res) => {
        if (!alive) return
        if (res.error) setError(res.error.message || 'failed to load standings')
        else if (res.data?.error) setError(res.data.error)
        else setData(res.data)
      })
      .catch((e) => { if (alive) setError(e?.message || 'failed to load standings') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [competitionCode])

  return { data, loading, error }
}

// Curated league list for the filter chips. Reads from sports_leagues
// (enabled rows only) and caches the result for the page lifetime.
export function useLeagues() {
  const { user } = useAuth()
  const [leagues, setLeagues] = useState([])
  const [loading, setLoading] = useState(true)
  const [tableMissing, setTableMissing] = useState(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    const { data, error } = await supabase
      .from('sports_leagues')
      .select('id, slug, name, name_ar, country, country_code, logo_url, category, display_order, provider_code')
      .eq('enabled', true)
      .order('display_order', { ascending: true })
    if (error) {
      if (
        error.code === 'PGRST205'
        || /schema cache/i.test(error.message || '')
        || /does not exist/i.test(error.message || '')
      ) setTableMissing(true)
    } else {
      setLeagues(data || [])
      setTableMissing(false)
    }
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  return { leagues, loading, tableMissing, refetch: fetch }
}

// Per-user favourite-leagues. Default filter narrows fixtures to
// this set when non-empty; an empty set means "show me everything".
// Same optimistic-mutation pattern as useSavedArticleIds /
// useMutedSources in the news module.
export function useFavouriteLeagues() {
  const { user } = useAuth()
  const [favouriteIds, setFavouriteIds] = useState(() => new Set())
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    const { data, error } = await supabase
      .from('sports_user_leagues')
      .select('league_id')
    if (!error && data) {
      setFavouriteIds(new Set(data.map((r) => r.league_id)))
    }
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  const toggle = useCallback(async (leagueId) => {
    if (!user || !leagueId) return
    const wasFavourite = favouriteIds.has(leagueId)
    setFavouriteIds((prev) => {
      const next = new Set(prev)
      if (wasFavourite) next.delete(leagueId)
      else next.add(leagueId)
      return next
    })
    try {
      if (wasFavourite) {
        const { error } = await supabase
          .from('sports_user_leagues')
          .delete()
          .eq('league_id', leagueId)
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('sports_user_leagues')
          .upsert(
            { user_id: user.id, league_id: leagueId },
            { onConflict: 'user_id,league_id', ignoreDuplicates: true },
          )
        if (error) throw error
      }
    } catch (_e) {
      // Rollback on failure.
      setFavouriteIds((prev) => {
        const next = new Set(prev)
        if (wasFavourite) next.add(leagueId)
        else next.delete(leagueId)
        return next
      })
    }
  }, [user, favouriteIds])

  return { favouriteIds, loading, toggle, refetch: fetch }
}

// Main fixture-list hook. Pulls everything from `live` + the next 7
// days (kickoff ≥ now - 4h so a match that just kicked off still
// shows in the live bucket while the upstream catalog catches up).
// `leagueIds` is an optional array filter; null/empty means "all".
// Returns the rows grouped into live / today / tomorrow / later — the
// page renders each group as its own section. Live rows are also
// re-fetched on a fast interval while at least one is in play.
const SEVEN_DAYS_MS = 7 * 24 * 3600 * 1000
const LIVE_POLL_INTERVAL_MS = 30_000

// `days` controls the forward window the page reads from sports_fixtures.
// 14 (was 7) so off-season and tournament-eve gaps still surface upcoming
// matches — at the time this was bumped, the next visible kickoff was
// 10 days out (a World Cup opener) and the page had been rendering empty.
export function useFixtures({ leagueIds, teamIds, days = 14 } = {}) {
  const { user } = useAuth()
  const [fixtures, setFixtures] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tableMissing, setTableMissing] = useState(false)

  // Stable identity for the filters so the effect doesn't re-fire on
  // every re-render with a fresh array literal.
  const leagueIdsKey = useMemo(
    () => (leagueIds && leagueIds.length ? [...leagueIds].sort().join(',') : ''),
    [leagueIds],
  )
  const teamIdsKey = useMemo(
    () => (teamIds && teamIds.length ? [...teamIds].sort().join(',') : ''),
    [teamIds],
  )

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    setError(null)
    const since = new Date(Date.now() - 4 * 3600 * 1000).toISOString()
    const until = new Date(Date.now() + days * 24 * 3600 * 1000).toISOString()
    let q = supabase
      .from('sports_fixtures')
      .select('id, api_id, kickoff_at, status, elapsed_minutes, home_team_id, home_team, home_logo_url, home_score, away_team_id, away_team, away_logo_url, away_score, venue, round, league:sports_leagues(id, slug, name, name_ar, country_code, logo_url)')
      .gte('kickoff_at', since)
      .lte('kickoff_at', until)
      .order('kickoff_at', { ascending: true })
    if (leagueIdsKey) {
      const ids = leagueIdsKey.split(',')
      q = q.in('league_id', ids)
    }
    if (teamIdsKey) {
      // Match where the favourite team is either home or away. PostgREST
      // accepts an OR composed of two .in()s in the same .or() clause.
      const teamCsv = teamIdsKey.replaceAll(',', ',')
      q = q.or(`home_team_id.in.(${teamCsv}),away_team_id.in.(${teamCsv})`)
    }
    const { data, error: err } = await q
    if (err) {
      if (
        err.code === 'PGRST205'
        || /schema cache/i.test(err.message || '')
        || /does not exist/i.test(err.message || '')
      ) setTableMissing(true)
      else setError(err.message)
    } else {
      setFixtures(data || [])
      setTableMissing(false)
    }
    setLoading(false)
  }, [user, days, leagueIdsKey, teamIdsKey])

  useEffect(() => { fetch() }, [fetch])

  // Fast-refresh while any fixture is live. Reads our own DB — the
  // edge function's 6h cron handles the actual upstream pull. If
  // we're caught between syncs, the score might lag up to ~6h, but
  // for v1 that's acceptable. Bumping cron cadence is the lever if
  // we want tighter live updates.
  const liveRef = useRef(false)
  liveRef.current = fixtures.some((f) => f.status === 'live')
  useEffect(() => {
    if (!liveRef.current) return undefined
    const id = setInterval(() => { fetch() }, LIVE_POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [fetch, fixtures])

  // Bucket fixtures into the four sections the page renders. The
  // bucketing happens client-side so it stays correct as time
  // advances (a fixture at 6 PM today becomes "tomorrow's match"
  // after midnight without a re-fetch).
  const buckets = useMemo(() => {
    const now = new Date()
    const startOfTomorrow = new Date(now)
    startOfTomorrow.setHours(0, 0, 0, 0)
    startOfTomorrow.setDate(startOfTomorrow.getDate() + 1)
    const startOfDayAfter = new Date(startOfTomorrow)
    startOfDayAfter.setDate(startOfDayAfter.getDate() + 1)

    const live = []
    const today = []
    const tomorrow = []
    const later = []
    for (const f of fixtures) {
      const kickoff = new Date(f.kickoff_at)
      if (f.status === 'live') {
        live.push(f)
        continue
      }
      if (f.status === 'finished') {
        // Finished matches only stay in "today" if the kickoff was
        // today — past finished from earlier days roll off via cron
        // retention. Anything still in the table that's finished and
        // matches "today" goes here so the user sees "FT 2 - 1".
        if (kickoff < startOfTomorrow) today.push(f)
        continue
      }
      if (kickoff < startOfTomorrow) today.push(f)
      else if (kickoff < startOfDayAfter) tomorrow.push(f)
      else later.push(f)
    }
    return { live, today, tomorrow, later }
  }, [fixtures])

  return { fixtures, buckets, loading, error, tableMissing, refetch: fetch }
}
